import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  People,
  SwapHoriz,
  Visibility,
  CheckCircle,
  Cancel,
  SportsTennis,
  Share,
} from '@mui/icons-material';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Tournament, TournamentParticipant, User } from '../../types';
import apiService from '../../services/api';

function participantDisplayLabel(participant: TournamentParticipant): string {
  const team = participant.team_name?.trim();
  const players = [participant.player1_name, participant.player2_name].filter(Boolean).join(' & ');
  if (team && players) return `${team} (${players})`;
  return team || players || `Participant #${participant.id}`;
}

// Extended interface for admin form with additional fields
interface TournamentFormData extends Partial<Tournament> {
  registration_deadline?: string;
  min_skill_level?: number;
  max_skill_level?: number;
}

export const AdminTournamentsPage: React.FC = () => {
  const { t } = useTranslation();
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [participantsDialog, setParticipantsDialog] = useState(false);
  const [matchesDialog, setMatchesDialog] = useState(false);
  const [editingTournament, setEditingTournament] = useState<Tournament | null>(null);
  const [selectedTournamentForStatus, setSelectedTournamentForStatus] = useState<Tournament | null>(null);
  const [selectedTournamentForParticipants, setSelectedTournamentForParticipants] = useState<Tournament | null>(null);
  const [selectedTournamentForMatches, setSelectedTournamentForMatches] = useState<Tournament | null>(null);
  const [participants, setParticipants] = useState<TournamentParticipant[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('Confirm Action');
  const [formData, setFormData] = useState<TournamentFormData>({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    max_participants: 16,
    min_skill_level: 1,
    max_skill_level: 10,
    format: 'round_robin',
    status: 'upcoming',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [copySuccessId, setCopySuccessId] = useState<number | null>(null);
  const { user } = useAuth();

  const handleCopyLink = (tournamentId: number) => {
    const url = `${window.location.origin}/t/${tournamentId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccessId(tournamentId);
      setTimeout(() => setCopySuccessId(null), 2000);
    });
  };

  useEffect(() => {
    if (user?.is_admin) {
      loadTournaments();
      loadUsers();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTournamentForParticipants) {
      loadParticipants(selectedTournamentForParticipants.id);
    }
  }, [selectedTournamentForParticipants]);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllTournaments();
      setTournaments(response.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await apiService.getAllUsers();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    }
  };

  const loadParticipants = async (tournamentId: number) => {
    try {
      setLoadingParticipants(true);
      const response = await apiService.getTournamentParticipants(tournamentId);
      setParticipants(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load participants');
    } finally {
      setLoadingParticipants(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.description?.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.start_date) {
      newErrors.start_date = 'Start date is required';
    }

    if (!formData.end_date) {
      newErrors.end_date = 'End date is required';
    }

    if (!formData.registration_deadline) {
      newErrors.registration_deadline = 'Registration deadline is required';
    }

    if ((formData.max_participants || 0) < 2) {
      newErrors.max_participants = 'Max participants must be at least 2';
    }

    if ((formData.min_skill_level || 0) < 1 || (formData.min_skill_level || 0) > 10) {
      newErrors.min_skill_level = 'Min skill level must be between 1 and 10';
    }

    if ((formData.max_skill_level || 0) < 1 || (formData.max_skill_level || 0) > 10) {
      newErrors.max_skill_level = 'Max skill level must be between 1 and 10';
    }

    if ((formData.min_skill_level || 0) > (formData.max_skill_level || 0)) {
      newErrors.max_skill_level = 'Max skill level must be greater than min skill level';
    }

    if (formData.start_date && formData.end_date && new Date(formData.start_date) >= new Date(formData.end_date)) {
      newErrors.end_date = 'End date must be after start date';
    }

    if (formData.registration_deadline && formData.start_date && new Date(formData.registration_deadline) >= new Date(formData.start_date)) {
      newErrors.registration_deadline = 'Registration deadline must be before start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      if (editingTournament) {
        await apiService.updateTournament(editingTournament.id, formData);
      } else {
        await apiService.createTournament(formData);
      }

      setOpenDialog(false);
      setEditingTournament(null);
      resetForm();
      loadTournaments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save tournament');
    }
  };

  const handleEdit = (tournament: Tournament) => {
    setEditingTournament(tournament);
    setFormData({
      name: tournament.name,
      description: tournament.description,
      start_date: tournament.start_date,
      end_date: tournament.end_date,
      registration_deadline: '', // Default value since it's not in the interface
      max_participants: tournament.max_participants || 16,
      min_skill_level: 1, // Default value since it's not in the interface
      max_skill_level: 10, // Default value since it's not in the interface
      format: (tournament.format || 'round_robin') as Tournament['format'],
      status: tournament.status as 'upcoming' | 'registration' | 'active' | 'completed' | 'cancelled',
    });
    setOpenDialog(true);
  };

  const handleDelete = (tournament: Tournament) => {
    setConfirmTitle(t('adminTournament.confirmDeleteTitle'));
    setConfirmMessage(
      t('adminTournament.confirmDeleteMessage', { name: tournament.name })
    );
    setConfirmAction(() => async () => {
      try {
        await apiService.deleteTournament(tournament.id);
        loadTournaments();
        setOpenConfirmDialog(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to delete tournament');
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleManageParticipants = (tournament: Tournament) => {
    setSelectedTournamentForParticipants(tournament);
    setParticipantsDialog(true);
  };

  const handleManageMatches = (tournament: Tournament) => {
    setSelectedTournamentForMatches(tournament);
    setMatchesDialog(true);
  };

  const handleApproveParticipant = async (participantId: number) => {
    if (!selectedTournamentForParticipants) return;
    try {
      await apiService.approveTournamentParticipant(selectedTournamentForParticipants.id, participantId);
      await loadParticipants(selectedTournamentForParticipants.id);
      await loadTournaments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to approve participant');
    }
  };

  const handleRejectParticipant = async (participantId: number) => {
    if (!selectedTournamentForParticipants) return;
    setConfirmMessage('Are you sure you want to reject this participant?');
    setConfirmAction(() => async () => {
      try {
        await apiService.rejectTournamentParticipant(selectedTournamentForParticipants!.id, participantId);
        await loadParticipants(selectedTournamentForParticipants!.id);
        await loadTournaments();
        setOpenConfirmDialog(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to reject participant');
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleRemoveParticipant = (participant: TournamentParticipant) => {
    if (!selectedTournamentForParticipants) return;
    setConfirmTitle(t('adminTournament.confirmRemoveTitle'));
    setConfirmMessage(
      t('adminTournament.confirmRemoveMessage', {
        label: participantDisplayLabel(participant),
        tournament: selectedTournamentForParticipants.name,
      })
    );
    setConfirmAction(() => async () => {
      try {
        await apiService.removeTournamentParticipant(
          selectedTournamentForParticipants!.id,
          participant.id
        );
        await loadParticipants(selectedTournamentForParticipants!.id);
        await loadTournaments();
        setOpenConfirmDialog(false);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to remove participant');
      }
    });
    setOpenConfirmDialog(true);
  };

  const handleStatusChange = (tournament: Tournament) => {
    setSelectedTournamentForStatus(tournament);
    setStatusDialog(true);
  };

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedTournamentForStatus) return;

    try {
      await apiService.updateTournament(selectedTournamentForStatus.id, {
        status: newStatus as 'upcoming' | 'registration' | 'active' | 'completed' | 'cancelled',
      });
      setStatusDialog(false);
      setSelectedTournamentForStatus(null);
      loadTournaments();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update tournament status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      registration_deadline: '',
      max_participants: 16,
      min_skill_level: 1,
      max_skill_level: 10,
      format: 'round_robin',
      status: 'upcoming',
    });
    setErrors({});
  };

  const handleOpenDialog = () => {
    setEditingTournament(null);
    resetForm();
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTournament(null);
    resetForm();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'info';
      case 'registration':
        return 'warning';
      case 'active':
        return 'success';
      case 'completed':
        return 'primary';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFormatLabel = (format: string) => {
    switch (format) {
      case 'round_robin':
        return 'Round Robin';
      case 'single_elimination':
        return 'Single Elimination';
      case 'double_elimination':
        return 'Double Elimination';
      case 'swiss_system':
        return 'Swiss System';
      case 'league':
        return 'League';
      case 'flex_league':
        return 'Flex League';
      case 'montante_descendante':
        return 'Montante/Descendante';
      case 'americano':
        return 'Americano';
      case 'mexicano':
        return 'Mexicano';
      default:
        return format;
    }
  };

  if (!user?.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6">Access Denied</Typography>
          <Typography>You need admin privileges to access this section.</Typography>
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingSpinner message="Loading tournaments..." />;
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Tournament Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create, edit, and manage tournaments with full administrative control.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
        >
          Create Tournament
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tournaments Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Format</TableCell>
                  <TableCell>Dates</TableCell>
                  <TableCell>Participants</TableCell>
                  <TableCell>Skill Range</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tournaments.map((tournament) => (
                  <TableRow key={tournament.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {tournament.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tournament.description ? tournament.description.substring(0, 50) + '...' : 'No description'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={tournament.status}
                        color={getStatusColor(tournament.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getFormatLabel(tournament.format || 'round_robin')}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          Start: {new Date(tournament.start_date).toLocaleDateString()}
                        </Typography>
                        <Typography variant="body2">
                          End: {new Date(tournament.end_date).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <People />
                        {tournament.current_participants}/{tournament.max_participants}
                      </Box>
                    </TableCell>
                    <TableCell>
                      Any
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(tournament.id)}
                          title={copySuccessId === tournament.id ? 'Link copied!' : 'Copy link'}
                        >
                          <Share />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(tournament)}
                          title="Edit"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleManageParticipants(tournament)}
                          title="Manage Participants"
                        >
                          <People />
                        </IconButton>
                        {tournament.format === 'flex_league' && (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleManageMatches(tournament)}
                            title="Manage Matches"
                          >
                            <SportsTennis />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleStatusChange(tournament)}
                          title="Change Status"
                        >
                          <SwapHoriz />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(tournament)}
                          title="Delete"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Tournament Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTournament ? 'Edit Tournament' : 'Create New Tournament'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Tournament Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                error={!!errors.description}
                helperText={errors.description}
                multiline
                rows={3}
                fullWidth
              />
              <Box display="flex" gap={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  error={!!errors.start_date}
                  helperText={errors.start_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  error={!!errors.end_date}
                  helperText={errors.end_date}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box display="flex" gap={2}>
                <TextField
                  label="Registration Deadline"
                  type="date"
                  value={formData.registration_deadline}
                  onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                  error={!!errors.registration_deadline}
                  helperText={errors.registration_deadline}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Max Participants"
                  type="number"
                  value={formData.max_participants}
                  onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) })}
                  error={!!errors.max_participants}
                  helperText={errors.max_participants}
                  fullWidth
                  inputProps={{ min: 2 }}
                />
              </Box>
              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>Format</InputLabel>
                  <Select
                    value={formData.format}
                    label="Format"
                    onChange={(e) => setFormData({ ...formData, format: e.target.value as any })}
                  >
                    <MenuItem value="round_robin">Round Robin</MenuItem>
                    <MenuItem value="single_elimination">Single Elimination</MenuItem>
                    <MenuItem value="double_elimination">Double Elimination</MenuItem>
                    <MenuItem value="swiss_system">Swiss System</MenuItem>
                    <MenuItem value="league">League</MenuItem>
                    <MenuItem value="flex_league">Flex League</MenuItem>
                    <MenuItem value="montante_descendante">Montante/Descendante</MenuItem>
                    <MenuItem value="americano">Americano</MenuItem>
                    <MenuItem value="mexicano">Mexicano</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                  >
                    <MenuItem value="upcoming">Upcoming</MenuItem>
                    <MenuItem value="registration">Registration</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box display="flex" gap={2}>
                <TextField
                  label="Min Skill Level"
                  type="number"
                  value={formData.min_skill_level}
                  onChange={(e) => setFormData({ ...formData, min_skill_level: parseInt(e.target.value) })}
                  error={!!errors.min_skill_level}
                  helperText={errors.min_skill_level}
                  fullWidth
                  inputProps={{ min: 1, max: 10 }}
                />
                <TextField
                  label="Max Skill Level"
                  type="number"
                  value={formData.max_skill_level}
                  onChange={(e) => setFormData({ ...formData, max_skill_level: parseInt(e.target.value) })}
                  error={!!errors.max_skill_level}
                  helperText={errors.max_skill_level}
                  fullWidth
                  inputProps={{ min: 1, max: 10 }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTournament ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Participants Management Dialog */}
      <Dialog open={participantsDialog} onClose={() => setParticipantsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Manage Participants - {selectedTournamentForParticipants?.name}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={() => {
                // TODO: Add participant dialog
                setError('Add participant feature coming soon');
              }}
            >
              Add Participant
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingParticipants ? (
            <Box display="flex" justifyContent="center" p={3}>
              <LoadingSpinner message="Loading participants..." />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Team Name</TableCell>
                    <TableCell>Player 1</TableCell>
                    <TableCell>Player 2</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Applied At</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {participants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography color="text.secondary">No participants yet</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    participants.map((participant) => (
                      <TableRow key={participant.id}>
                        <TableCell>{participant.team_name || 'N/A'}</TableCell>
                        <TableCell>{participant.player1_name || `User ${participant.player1_id}`}</TableCell>
                        <TableCell>{participant.player2_name || (participant.player2_id ? `User ${participant.player2_id}` : 'N/A')}</TableCell>
                        <TableCell>
                          <Chip
                            label={participant.status}
                            size="small"
                            color={
                              participant.status === 'approved' ? 'success' :
                              participant.status === 'pending' ? 'warning' :
                              'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {participant.applied_at ? new Date(participant.applied_at).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {participant.status === 'pending' && (
                              <>
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleApproveParticipant(participant.id)}
                                  title="Approve"
                                >
                                  <CheckCircle />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRejectParticipant(participant.id)}
                                  title="Reject"
                                >
                                  <Cancel />
                                </IconButton>
                              </>
                            )}
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveParticipant(participant)}
                              title="Remove"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParticipantsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Matches Management Dialog */}
      <Dialog open={matchesDialog} onClose={() => setMatchesDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Manage Matches - {selectedTournamentForMatches?.name}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 2 }}>
            Match management for tournaments will be available soon. For Flex League tournaments, you can manage matches from the tournament details page.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMatchesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
          setConfirmAction(null);
        }}
        onConfirm={() => {
          if (confirmAction) {
            confirmAction();
          }
        }}
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={t('adminTournament.confirm')}
        cancelLabel={t('adminTournament.cancel')}
        severity="error"
      />

      {/* Status Change Dialog */}
      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Tournament Status</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body1" gutterBottom>
              Current Status: <strong>{selectedTournamentForStatus?.status}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Select new status:
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              {['upcoming', 'registration', 'active', 'completed', 'cancelled'].map((status) => (
                <Button
                  key={status}
                  variant={selectedTournamentForStatus?.status === status ? 'contained' : 'outlined'}
                  onClick={() => handleUpdateStatus(status)}
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
